import type { IAggFunc, IAggFuncParams } from 'ag-grid-community';
import Big from 'big.js';
import type { SizeColumnParams } from '../columns';
import { getCustomColDefProperties } from '../columns/baseColumn';
import type { SizeValue } from './types';

/** Empty result, don't agg above it */
const EMPTY_NO_AGG_RESULT: SizeValue = { value: '', currency: '', aggregate: false };

/**
 * Aggregates SizeValues originating from the size column
 */
export const sizeAggFunc: IAggFunc<unknown, SizeValue> = (aggValue: IAggFuncParams<unknown, SizeValue>): SizeValue => {
  const columnParams: SizeColumnParams | undefined = getCustomColDefProperties(
    aggValue.column?.getColDef()
  )?.talosColumnParams;
  const { aggregateIf } = columnParams ?? {};
  // If we have an aggregateIf function, and it returns false, then we should not aggregate this value.
  if (aggregateIf && !aggregateIf(aggValue)) {
    return EMPTY_NO_AGG_RESULT;
  }
  const currencyToSum = new Map<string, SizeValue>();
  const { values: unsanitizedValues } = aggValue;
  const values = unsanitizedValues.filter((value: SizeValue | null): value is SizeValue => value != null);
  const firstCurrency = values.find(item => item.currency)?.currency;
  for (const { value, currency, increment, aggregate } of values) {
    // Return an un-aggregateable value immediately once we know that one of our received values is itself signaling that we can't proceed with the aggregation.
    if (aggregate === false) {
      return EMPTY_NO_AGG_RESULT;
    }

    // If the value is empty, we do not care about this entry, and just continue to the next
    if (value == null || value === '') {
      continue;
    }

    // At this point there is a value, so we do care about the entry. If the currency here is undefined, we abort.
    // This also means that we _do not support aggregating undefined currencies_ (eg "Unit" positions).
    if (currency === undefined || currency === '') {
      return EMPTY_NO_AGG_RESULT;
    }

    try {
      let bigValue = Big(value);
      // try to get the existing value for this currency and add the incoming value to it
      const maybeExistingBig = currencyToSum.get(currency);
      if (maybeExistingBig?.value) {
        bigValue = bigValue.plus(maybeExistingBig.value ?? '0');
      }

      currencyToSum.set(currency, { value: bigValue, currency, increment });
      if (currencyToSum.size > 1) {
        return EMPTY_NO_AGG_RESULT; // return early immediately once we know that theres more than one currency in this column
      }
    } catch (e) {
      return EMPTY_NO_AGG_RESULT; // fail gracefully and return an empty result
    }
  }

  if (currencyToSum.size === 1) {
    return currencyToSum.values().next().value ?? EMPTY_NO_AGG_RESULT;
  } else {
    // currencyToSum is added to with currencies for populated values. firstCurrency is just the first currency we find in the values array.
    // Sometimes, the backend sends us the equivalent of { value: null, currency: "BTC" }, in which case it still makes sense to carry that BTC currency forward in the aggregation.
    if (currencyToSum.size === 0) {
      return { value: '', currency: firstCurrency ?? '', aggregate: true };
    }
    return EMPTY_NO_AGG_RESULT;
  }
};
